<template>
  <div class="my-applications">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-info">
            <h1 class="page-title">
              <i class="el-icon-document"></i>
              我的申请记录
            </h1>
            <p class="page-subtitle">查看门店入驻申请的进度和结果</p>
          </div>
        </div>
        <div class="header-actions">
          <el-button size="medium" @click="loadApplications">
            <i class="el-icon-refresh"></i> 刷新数据
          </el-button>
          <el-button type="primary" size="medium" @click="handleNewApplication">
            <i class="el-icon-plus"></i> 新申请
          </el-button>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 筛选区域 -->
      <div class="filter-section">
        <div class="filter-card">
          <el-form :model="queryForm" :inline="true" class="filter-form">
            <el-form-item label="申请状态">
              <el-select v-model="queryForm.status" placeholder="全部状态" clearable>
                <el-option label="待审核" :value="0">
                  <span style="color: #e6a23c">
                    <i class="el-icon-time"></i> 待审核
                  </span>
                </el-option>
                <el-option label="已通过" :value="1">
                  <span style="color: #67c23a">
                    <i class="el-icon-success"></i> 已通过
                  </span>
                </el-option>
                <el-option label="已拒绝" :value="2">
                  <span style="color: #f56c6c">
                    <i class="el-icon-error"></i> 已拒绝
                  </span>
                </el-option>
              </el-select>
            </el-form-item>
            
            <el-form-item>
              <el-button type="primary" @click="handleSearch" :loading="loading">
                <i class="el-icon-search"></i> 搜索
              </el-button>
              <el-button @click="handleReset">
                <i class="el-icon-refresh-left"></i> 重置
              </el-button>
            </el-form-item>
          </el-form>
        </div>
      </div>

      <!-- 申请列表 -->
      <div class="applications-section">
        <div v-if="loading" class="loading-container">
          <el-skeleton :rows="3" animated />
        </div>
        
        <div v-else-if="applicationList.length === 0" class="empty-container">
          <div class="empty-content">
            <i class="el-icon-document-remove"></i>
            <p>暂无申请记录</p>
            <el-button type="primary" @click="handleNewApplication">
              <i class="el-icon-plus"></i> 立即申请
            </el-button>
          </div>
        </div>

        <div v-else class="application-cards">
          <div 
            v-for="application in applicationList" 
            :key="application.id" 
            class="application-card"
            :class="getCardClass(application.status)"
          >
            <div class="card-header">
              <div class="application-info">
                <h3 class="application-title">{{ application.store_name }}</h3>
                <div class="application-meta">
                  <span class="application-id">申请编号：{{ application.id }}</span>
                  <span class="application-time">申请时间：{{ application.created_at | formatDateTime }}</span>
                </div>
              </div>
              <div class="status-badge" :class="getStatusClass(application.status)">
                <i :class="getStatusIcon(application.status)"></i>
                {{ getStatusText(application.status) }}
              </div>
            </div>

            <div class="card-content">
              <div class="info-row">
                <div class="info-item">
                  <label>申请人：</label>
                  <span>{{ application.applicant_name }}</span>
                </div>
                <div class="info-item">
                  <label>联系电话：</label>
                  <span>{{ application.applicant_phone }}</span>
                </div>
                <div class="info-item">
                  <label>门店类型：</label>
                  <span>{{ application.store_type === 1 ? '实体门店' : '代理商' }}</span>
                </div>
              </div>
              
              <div class="info-row">
                <div class="info-item full-width">
                  <label>门店地址：</label>
                  <span>{{ `${application.province} ${application.city} ${application.district} ${application.address}` }}</span>
                </div>
              </div>

              <div v-if="application.audit_reason" class="audit-reason">
                <label>审核意见：</label>
                <span>{{ application.audit_reason }}</span>
              </div>

              <div v-if="application.audit_time" class="audit-time">
                <label>审核时间：</label>
                <span>{{ application.audit_time | formatDateTime }}</span>
              </div>
            </div>

            <div class="card-actions">
              <el-button size="small" @click="handleViewDetail(application)">
                <i class="el-icon-view"></i> 查看详情
              </el-button>
              
              <el-button 
                v-if="application.status === 1 && application.store_id" 
                size="small" 
                type="primary" 
                @click="handleViewStore(application)"
              >
                <i class="el-icon-s-shop"></i> 查看门店
              </el-button>
              
              <el-button 
                v-if="application.status === 2" 
                size="small" 
                type="warning" 
                @click="handleReapply(application)"
              >
                <i class="el-icon-refresh-right"></i> 重新申请
              </el-button>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div v-if="total > 0" class="pagination-section">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="queryForm.page"
            :page-sizes="[5, 10, 20, 50]"
            :page-size="queryForm.page_size"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            background
          >
          </el-pagination>
        </div>
      </div>
    </div>

    <!-- 详情对话框 -->
    <el-dialog
      :visible.sync="detailVisible"
      width="60%"
      title="申请详情"
      :before-close="handleCloseDetail"
    >
      <div v-if="currentApplication" class="detail-content">
        <!-- 基本信息 -->
        <div class="detail-section">
          <h4>基本信息</h4>
          <div class="detail-grid">
            <div class="detail-item">
              <label>申请人姓名：</label>
              <span>{{ currentApplication.applicant_name }}</span>
            </div>
            <div class="detail-item">
              <label>联系电话：</label>
              <span>{{ currentApplication.applicant_phone }}</span>
            </div>
            <div class="detail-item">
              <label>门店名称：</label>
              <span>{{ currentApplication.store_name }}</span>
            </div>
            <div class="detail-item">
              <label>门店类型：</label>
              <span>{{ currentApplication.store_type === 1 ? '实体门店' : '代理商' }}</span>
            </div>
            <div class="detail-item">
              <label>详细地址：</label>
              <span>{{ `${currentApplication.province} ${currentApplication.city} ${currentApplication.district} ${currentApplication.address}` }}</span>
            </div>
          </div>
        </div>

        <!-- 证件材料 -->
        <div class="detail-section">
          <h4>证件材料</h4>
          <div class="certificate-grid">
            <div v-if="currentApplication.business_license" class="certificate-item">
              <label>营业执照</label>
              <img 
                :src="currentApplication.business_license" 
                @click="previewImage(currentApplication.business_license)"
                class="certificate-image"
                alt="营业执照"
              >
            </div>
            <div v-if="currentApplication.id_card_front" class="certificate-item">
              <label>身份证正面</label>
              <img 
                :src="currentApplication.id_card_front" 
                @click="previewImage(currentApplication.id_card_front)"
                class="certificate-image"
                alt="身份证正面"
              >
            </div>
            <div v-if="currentApplication.id_card_back" class="certificate-item">
              <label>身份证背面</label>
              <img 
                :src="currentApplication.id_card_back" 
                @click="previewImage(currentApplication.id_card_back)"
                class="certificate-image"
                alt="身份证背面"
              >
            </div>
          </div>
        </div>

        <!-- 申请说明 -->
        <div v-if="currentApplication.application_reason" class="detail-section">
          <h4>申请说明</h4>
          <p class="application-reason">{{ currentApplication.application_reason }}</p>
        </div>

        <!-- 审核信息 -->
        <div v-if="currentApplication.audit_time" class="detail-section">
          <h4>审核信息</h4>
          <div class="audit-info">
            <div class="audit-status" :class="getStatusClass(currentApplication.status)">
              <i :class="getStatusIcon(currentApplication.status)"></i>
              {{ getStatusText(currentApplication.status) }}
            </div>
            <div class="audit-time">审核时间：{{ currentApplication.audit_time | formatDateTime }}</div>
            <div v-if="currentApplication.audit_reason" class="audit-reason">
              审核意见：{{ currentApplication.audit_reason }}
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 图片预览对话框 -->
    <el-dialog :visible.sync="imagePreviewVisible" width="60%" center>
      <img :src="previewImageUrl" style="width: 100%" alt="预览图片">
    </el-dialog>
  </div>
</template>

<script>
import { getMyApplications } from '@/api/store'

export default {
  name: 'MyApplications',
  
  data() {
    return {
      loading: false,
      applicationList: [],
      total: 0,
      queryForm: {
        page: 1,
        page_size: 10,
        status: null
      },
      detailVisible: false,
      currentApplication: null,
      imagePreviewVisible: false,
      previewImageUrl: ''
    }
  },
  
  mounted() {
    this.loadApplications()
  },
  
  methods: {
    // 加载申请列表
    async loadApplications() {
      this.loading = true
      try {
        const response = await getMyApplications(this.queryForm)
        this.applicationList = response.data.list || []
        this.total = response.data.pagination?.total || 0
      } catch (error) {
        console.error('获取申请记录失败:', error)
        this.$message.error('获取申请记录失败')
      } finally {
        this.loading = false
      }
    },
    
    // 搜索
    handleSearch() {
      this.queryForm.page = 1
      this.loadApplications()
    },
    
    // 重置
    handleReset() {
      this.queryForm = {
        page: 1,
        page_size: 10,
        status: null
      }
      this.loadApplications()
    },
    
    // 分页大小改变
    handleSizeChange(val) {
      this.queryForm.page_size = val
      this.queryForm.page = 1
      this.loadApplications()
    },
    
    // 当前页改变
    handleCurrentChange(val) {
      this.queryForm.page = val
      this.loadApplications()
    },
    
    // 新申请
    handleNewApplication() {
      this.$router.push('/store-application')
    },
    
    // 查看详情
    handleViewDetail(application) {
      this.currentApplication = application
      this.detailVisible = true
    },
    
    // 关闭详情
    handleCloseDetail() {
      this.detailVisible = false
      this.currentApplication = null
    },
    
    // 查看门店
    handleViewStore(application) {
      this.$router.push(`/my-stores/${application.store_id}`)
    },
    
    // 重新申请
    handleReapply(application) {
      this.$confirm('确定要基于此申请重新提交吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      }).then(() => {
        // 跳转到申请页面，并预填信息
        this.$router.push({
          path: '/store-application',
          query: { reapply: application.id }
        })
      })
    },
    
    // 预览图片
    previewImage(url) {
      this.previewImageUrl = url
      this.imagePreviewVisible = true
    },
    
    // 获取卡片样式类
    getCardClass(status) {
      switch (status) {
        case 0: return 'status-pending'
        case 1: return 'status-approved'
        case 2: return 'status-rejected'
        default: return ''
      }
    },
    
    // 获取状态样式类
    getStatusClass(status) {
      switch (status) {
        case 0: return 'status-pending'
        case 1: return 'status-approved'
        case 2: return 'status-rejected'
        default: return 'status-pending'
      }
    },
    
    // 获取状态图标
    getStatusIcon(status) {
      switch (status) {
        case 0: return 'el-icon-time'
        case 1: return 'el-icon-success'
        case 2: return 'el-icon-error'
        default: return 'el-icon-time'
      }
    },
    
    // 获取状态文本
    getStatusText(status) {
      switch (status) {
        case 0: return '待审核'
        case 1: return '已通过'
        case 2: return '已拒绝'
        default: return '未知状态'
      }
    }
  },
  
  filters: {
    formatDateTime(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
/* 页面基础样式 */
.my-applications {
  min-height: 100vh;
  background: #f5f7fa;
}

/* 页面头部 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 40px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  flex: 1;
}

.header-info .page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.header-info .page-title i {
  font-size: 32px;
  color: white;
}

.header-info .page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
  color: white;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.header-actions .el-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 500;
}

.header-actions .el-button--primary {
  background: rgba(255, 255, 255, 0.95);
  color: #667eea;
  border-color: transparent;
}

.header-actions .el-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.header-actions .el-button--primary:hover {
  background: white;
  color: #667eea;
}

/* 主内容区域 */
.main-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
}

/* 筛选区域 */
.filter-section {
  margin-bottom: 30px;
}

.filter-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
}

/* 空状态 */
.empty-container {
  background: white;
  border-radius: 16px;
  padding: 60px 20px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.empty-content i {
  font-size: 64px;
  color: #dcdfe6;
  margin-bottom: 16px;
}

.empty-content p {
  font-size: 16px;
  color: #909399;
  margin-bottom: 20px;
}

/* 申请卡片 */
.application-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-bottom: 30px;
}

.application-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
  transition: all 0.3s ease;
  border-left: 4px solid #dcdfe6;
}

.application-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.application-card.status-pending {
  border-left-color: #e6a23c;
}

.application-card.status-approved {
  border-left-color: #67c23a;
}

.application-card.status-rejected {
  border-left-color: #f56c6c;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.application-title {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px 0;
}

.application-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
  color: #909399;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
}

.status-badge.status-pending {
  background: #fdf6ec;
  color: #e6a23c;
}

.status-badge.status-approved {
  background: #f0f9ff;
  color: #67c23a;
}

.status-badge.status-rejected {
  background: #fef0f0;
  color: #f56c6c;
}

.card-content {
  margin-bottom: 20px;
}

.info-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 12px;
}

.info-item {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-item label {
  color: #606266;
  margin-right: 8px;
  font-weight: 500;
  min-width: 80px;
}

.info-item span {
  color: #303133;
}

.audit-reason,
.audit-time {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f2f5;
  font-size: 14px;
}

.audit-reason label,
.audit-time label {
  color: #606266;
  font-weight: 500;
  margin-right: 8px;
}

.card-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #f0f2f5;
}

/* 详情对话框 */
.detail-content {
  max-height: 600px;
  overflow-y: auto;
}

.detail-section {
  margin-bottom: 24px;
}

.detail-section h4 {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f2f5;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.detail-item {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.detail-item label {
  color: #606266;
  margin-right: 8px;
  font-weight: 500;
  min-width: 100px;
}

.certificate-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.certificate-item {
  text-align: center;
}

.certificate-item label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #606266;
  margin-bottom: 8px;
}

.certificate-image {
  width: 120px;
  height: 80px;
  object-fit: cover;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
}

.certificate-image:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.application-reason {
  background: #fafbfc;
  padding: 12px;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.6;
  color: #606266;
  margin: 0;
}

.audit-info {
  background: #fafbfc;
  padding: 16px;
  border-radius: 8px;
}

.audit-info .audit-status {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.audit-info .audit-time,
.audit-info .audit-reason {
  font-size: 14px;
  color: #606266;
  margin-bottom: 4px;
}

/* 分页 */
.pagination-section {
  background: white;
  border-radius: 16px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-header {
    padding: 20px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .header-actions {
    width: 100%;
  }
  
  .header-actions .el-button {
    flex: 1;
  }
  
  .main-content {
    padding: 16px 20px;
  }
  
  .info-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .card-actions {
    flex-direction: column;
  }
  
  .detail-grid {
    grid-template-columns: 1fr;
  }
  
  .certificate-grid {
    grid-template-columns: 1fr;
  }
  
  .certificate-image {
    width: 200px;
    height: 120px;
  }
}
</style>
